import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { 
  Close,
  HelpOutline,
  AccountTree
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '@mui/material/styles';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';
import BrowserPanelAlert from '../components/BrowserPanelAlert';

/**
 * BrowserPanel Component
 *
 * Displays a tree view of projects, targets, and batches for browsing and filtering structures.
 * Supports multiple selection of batches for batch operations.
 *
 * @param {Object} props
 * @param {Array} props.treeData - Hierarchical tree data
 * @param {Array} props.treeSelection - Array of selected tree node IDs
 * @param {Function} props.onTreeSelectionChange - Callback when tree selection changes
 * @param {Array} props.expandedItems - Array of expanded tree node IDs
 * @param {Function} props.onExpandedItemsChange - Callback when expanded items change
 * @param {boolean} props.loading - Whether data is loading
 * @param {number} props.filteredCount - Number of filtered items
 * @param {number} props.selectedCount - Number of selected items
 * @param {number} props.totalCount - Total number of items
 * @param {Function} props.onClearFilter - Callback to clear the filter
 * @param {boolean} props.showHelp - Whether to show help dialog
 * @param {Function} props.onHelpToggle - Callback to toggle help dialog
 */
export default function BrowserPanel({
  treeData,
  treeSelection,
  onTreeSelectionChange,
  expandedItems,
  onExpandedItemsChange,
  loading,
  filteredCount,
  selectedCount,
  totalCount,
  onClearFilter,
  showHelp,
  onHelpToggle
}) {
  const theme = useTheme();
  // Render tree items recursively
  const renderTreeItems = (projects) => {
    // Sort projects alphabetically
    const sortedProjects = Object.values(projects).sort((a, b) => a.label.localeCompare(b.label));
    
    return sortedProjects.map((project) => {
      // Sort targets alphabetically
      const sortedTargets = Object.values(project.targets).sort((a, b) => a.label.localeCompare(b.label));
      
      return (
        <TreeItem
          key={project.id}
          itemId={project.id}
          label={project.label}
        >
          {sortedTargets.map((target) => {
            // Sort batches by date first, then alphabetically
            const sortedBatches = Object.values(target.batches).sort((a, b) => {
              // Sort by earliest date first
              if (a.earliestDate && b.earliestDate) {
                const dateCompare = a.earliestDate.localeCompare(b.earliestDate);
                if (dateCompare !== 0) return dateCompare;
              } else if (a.earliestDate) {
                return -1; // a has date, b doesn't - a comes first
              } else if (b.earliestDate) {
                return 1; // b has date, a doesn't - b comes first
              }
              // If dates are same or both missing, sort by label
              return a.label.localeCompare(b.label);
            });
            
            return (
              <TreeItem
                key={target.id}
                itemId={target.id}
                label={target.label}
              >
                {sortedBatches.map((batch) => (
                  <TreeItem
                    key={batch.id}
                    itemId={batch.id}
                    label={
                      batch.earliestDate 
                        ? (() => {
                            const date = new Date(batch.earliestDate);
                            const yy = date.getFullYear().toString().slice(-2);
                            const mm = (date.getMonth() + 1).toString().padStart(2, '0');
                            const dd = date.getDate().toString().padStart(2, '0');
                            return `${yy}${mm}${dd} ${batch.label} (${batch.items.length})`;
                          })()
                        : `${batch.label} (${batch.items.length})`
                    }
                  />
                ))}
              </TreeItem>
            );
          })}
        </TreeItem>
      );
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Browser Help Dialog */}
      <DraggableDialog
        open={showHelp}
        onClose={() => onHelpToggle(false)}
        title="Browser Help"
        variant="help"
        maxWidth="sm"
        fullWidth
      >
          <Box sx={{ 
            '& h3': { fontSize: theme.typography.fontSize?.base || '0.75rem', fontWeight: theme.typography.fontWeight.semibold, mt: 2, mb: 1 },
            '& h3:first-of-type': { mt: 0 },
            '& ul': { pl: theme.spacing(2), my: 0.5 },
            '& li': { fontSize: theme.typography.fontSize?.base || '0.75rem', mb: 0.5 },
            '& code': { 
              backgroundColor: 'grey.200', 
              px: 0.5, 
              py: 0.2, 
              borderRadius: 0.5,
              fontSize: theme.typography.fontSize?.xs || '0.65rem',
              fontFamily: 'monospace'
            },
            '& p': { fontSize: theme.typography.fontSize?.base || '0.75rem', my: 0.5 }
          }}>
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
{`### 1. Tree Hierarchy

The Browser organizes files in three levels:

- **Project** — Top-level grouping
  - **Target** — Protein target within the project
    - **Batch** — Collection of related structures
      - Format: \`YYMMDD batch_name (count)\`
      - Date prefix shows when structures were prepared
      - Number in parentheses shows file count

### 2. Navigate and Filter

- Click any tree node to expand it and filter the table
- **Multiple Selection**: Hold Ctrl/Cmd to select multiple batches
- Use Shift+Click to select a range of batches
- The main table shows files from all selected nodes
- All filtered files are automatically selected
- Click \`Clear\` to reset and show all files

### 3. Work with Filtered Files

Once filtered and selected, you can:
- Apply patterns using Pattern Extraction <i class="material-icons" style="font-size: 14px; vertical-align: middle;">auto_fix_high</i> panel
- Edit metadata using Metadata Editing <i class="material-icons" style="font-size: 14px; vertical-align: middle;">edit_note</i> panel
- Generate FMO inputs using FMO Input Generator <i class="material-icons" style="font-size: 14px; vertical-align: middle;">content_cut</i> panel
- When using Upload <i class="material-icons" style="font-size: 14px; vertical-align: middle;">cloud_upload</i> panel, the selected project/target/batch is automatically filled in`}
            </ReactMarkdown>
          </Box>
      </DraggableDialog>

      {/* Top Alert Box */}
      <BrowserPanelAlert 
        treeSelection={treeSelection}
        filteredCount={filteredCount}
        selectedCount={selectedCount}
        totalCount={totalCount}
        onClearFilter={onClearFilter}
      />

      {/* Tree View Section */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', px: theme.spacing(2), pb: theme.spacing(2) }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: theme.spacing(2) }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <SimpleTreeView
              multiSelect
              selectedItems={treeSelection}
              onSelectedItemsChange={onTreeSelectionChange}
              expandedItems={expandedItems}
              onExpandedItemsChange={(event, itemIds) => onExpandedItemsChange(itemIds)}
              sx={{
                '& .MuiTreeItem-label': {
                  fontSize: theme.typography.fontSize?.base || '0.75rem'
                }
              }}
            >
              {renderTreeItems(treeData)}
            </SimpleTreeView>
          </Box>
        )}
      </Box>
    </Box>
  );
}